/**
 * Canonical TipTap extension list for the 3mote MarkdownEditor.
 *
 * This is the single source of truth — both the live editor
 * (`MarkdownEditor.tsx`) and the round-trip spike test
 * (`__tests__/markdown-roundtrip.test.ts`) import from here so the
 * production behavior matches what tests validate.
 *
 * Allowed node set is dictated by ADR-003: bold, italic, strikethrough,
 * inline code, headings (h2/h3 only), bullet/ordered/task lists, code
 * blocks, links, tables, images, **mentions (Phase 3b)**. Everything
 * outside that list is dropped from StarterKit so we don't accept
 * content the markdown serializer will silently strip on save.
 *
 * The card + comment surfaces additionally carry the attachment-backed
 * `image`, `fileEmbed` chip and `videoEmbed` player; the wiki carries
 * those plus its slash-menu blocks; chat carries none of them (chat
 * attachments ride the attachments table, never the message body). The
 * authoritative per-surface node set is the fixture at
 * `richtext-surfaces.json`, which `richtext-surface-drift.test.ts`
 * asserts against the real ProseMirror schema built here.
 *
 * Underline is supported since ADR-046. ADR-003 excluded it because
 * markdown was the persisted format and GFM has no underline syntax;
 * ADR-039 made TipTap JSON canonical (markdown is derived-only), which
 * removed that round-trip constraint.
 *
 * Mentions (Phase 3b) round-trip as ordinary GFM markdown links with the
 * custom `mention://user/{uuid}` protocol — `[@David](mention://user/abc)`.
 * The editor renders them as styled chips (.mention-chip in
 * `src/styles/global.css`); the on-disk + on-wire representation is
 * pure markdown so AI agents can read and write mentions without parsing
 * HTML. See `src/editor/mention-extension.ts` for the parser/serializer
 * round-trip.
 */

import Image from '@tiptap/extension-image'
import type { MentionOptions } from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import Underline from '@tiptap/extension-underline'
import type { Extension, Mark, Node as TiptapNode } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import markdownItSub from 'markdown-it-sub'
import markdownItSup from 'markdown-it-sup'
import { Markdown } from 'tiptap-markdown'

import { ChatCodeBlockBehavior } from './chat-code-block-keymap'
import { EmptyParagraphHintExtension } from './empty-paragraph-hint-extension'
import { buildInlineCommentExtension } from './inline-comment-extension'
import { LinkWithClickToEdit } from './link-with-click-to-edit'
import { ListItemEditing } from './list-item-editing'
import {
  BoldWithFaithfulRules,
  CodeWithFaithfulRules,
  ItalicWithFaithfulRules,
  StrikeWithFaithfulRules,
} from './mark-input-rules'
import { buildMentionExtension } from './mention-extension'
import { DateChipView } from './nodes/date-chip/DateChipView'
import { buildDateChipExtension } from './nodes/date-chip/date-chip-extension'
import { ExpandSectionView } from './nodes/expand-section/ExpandSectionView'
import { buildExpandSectionExtension } from './nodes/expand-section/expand-section-extension'
import { CardFileEmbedView } from './nodes/file-embed/CardFileEmbedView'
import { FileEmbedView } from './nodes/file-embed/FileEmbedView'
import { buildCardFileEmbedExtension } from './nodes/file-embed/card-file-embed-extension'
import { buildFileEmbedExtension } from './nodes/file-embed/file-embed-extension'
import { CardImageNodeView } from './nodes/image-embed/CardImageNodeView'
import { WikiImageNodeView } from './nodes/image-embed/WikiImageNodeView'
import { buildCardImageExtension } from './nodes/image-embed/card-image-extension'
import { buildWikiImageExtension } from './nodes/image-embed/image-embed-extension'
import { CustomPanelView } from './nodes/panels/CustomPanelView'
import { buildCustomPanelExtension } from './nodes/panels/custom-panel-extension'
import { ErrorPanel } from './nodes/panels/error-panel-extension'
import { InfoPanel } from './nodes/panels/info-panel-extension'
import { NotePanel } from './nodes/panels/note-panel-extension'
import { SuccessPanel } from './nodes/panels/success-panel-extension'
import { WarningPanel } from './nodes/panels/warning-panel-extension'
import { StatusLozengeView } from './nodes/status-lozenge/StatusLozengeView'
import { buildStatusLozengeExtension } from './nodes/status-lozenge/status-lozenge-extension'
import { TableCellBackspace } from './nodes/table/cell-backspace'
import {
  WikiTable,
  WikiTableRow,
  buildWikiTableCell,
  buildWikiTableHeader,
} from './nodes/table/table-extensions'
import { CardVideoEmbedView } from './nodes/video-embed/CardVideoEmbedView'
import { VideoEmbedView } from './nodes/video-embed/VideoEmbedView'
import { buildVideoEmbedExtension } from './nodes/video-embed/video-embed-extension'
import { WorkItemEmbedView } from './nodes/work-item-embed/WorkItemEmbedView'
import { buildWorkItemEmbedExtension } from './nodes/work-item-embed/work-item-embed-extension'
import { ParagraphIdExtension } from './paragraph-id-extension'
import { buildSlashCommandExtension } from './slash-command-extension'
import { StickyStoredMarks } from './sticky-stored-marks'
import { TrailingParagraphExtension } from './trailing-paragraph-extension'
import { WikiEmojiMark } from './wiki-emoji-mark'
import { buildWikiLinkExtension } from './wikilink-extension'
import {
  type WorkItemAutodetectOptions,
  buildWorkItemAutodetectExtension,
} from './work-item-autodetect'

/**
 * Caller-facing surface tag — drives which optional extensions land on
 * top of the canonical set. `surface === 'wiki'` registers the slash-
 * command extension (Phase 4a); other surfaces (card descriptions,
 * comments, chat) leave it off so typing `/` is just typing `/`. The
 * default `'card'` matches every legacy call-site.
 */
export type EditorSurface = 'wiki' | 'card' | 'comment' | 'chat'

/**
 * Subscript with markdown round-trip wired up via tiptap-markdown's
 * per-extension storage hook. `~text~` ↔ `<sub>text</sub>` —
 * `markdown-it-sub` plugin parses on read, the `serialize` shape below
 * writes back on save.
 *
 * `mixable: true` lets the mark interleave with other inline marks
 * (Bold + Sub, Italic + Sub, etc.). `expelEnclosingWhitespace` trims
 * surrounding whitespace inside the mark boundary at serialization —
 * canonical tiptap-markdown mark-serializer behavior.
 */
const SubscriptWithMarkdown = Subscript.extend({
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '~',
          close: '~',
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {
          setup(md: { use(plugin: (md: unknown, ...args: unknown[]) => void): void }) {
            md.use(markdownItSub)
          },
        },
      },
    }
  },
})

/** Same pattern as SubscriptWithMarkdown — `^text^` ↔ `<sup>text</sup>`. */
const SuperscriptWithMarkdown = Superscript.extend({
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '^',
          close: '^',
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {
          setup(md: { use(plugin: (md: unknown, ...args: unknown[]) => void): void }) {
            md.use(markdownItSup)
          },
        },
      },
    }
  },
})

/**
 * Underline with the tiptap-markdown SERIALIZER hook only. Markdown has
 * no underline syntax, so the (dead — ADR-039 made JSON canonical on
 * every surface) markdown path emits inline HTML `<u>…</u>`; there is no
 * parse side. The hook exists so `tiptap-markdown` never throws on a
 * mark without a serializer spec, not to round-trip.
 */
const UnderlineWithMarkdown = Underline.extend({
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '<u>',
          close: '</u>',
          mixable: true,
          expelEnclosingWhitespace: true,
        },
      },
    }
  },
})

export interface BuildEditorExtensionsOptions {
  /**
   * Optional `suggestion` config for the Mention extension. The live
   * editor passes the popover-bound config from
   * `buildMentionSuggestion(...)`; the round-trip spike test omits it
   * (no autocomplete UI in headless tests, and the `[@Name](mention://user/UUID)`
   * round-trip is purely a parse + serialize concern).
   */
  mentionSuggestion?: MentionOptions['suggestion']
  /**
   * Whether to load the Table* extensions and (consequently) render the
   * `<TableBubbleMenu>` floating toolbar. Default `true` — full editor.
   *
   * Set to `false` for short-form surfaces (CommentComposer, comment
   * inline-edit) where tables aren't useful and the BubbleMenu's tippy.js
   * popper has been seen to race against ProseMirror's DOM teardown when
   * the host clears the editor on submit (`removeChild on Node`). R-2
   * caught this on Post in Phase 2d / 3b / 3c — the cleanest fix is to
   * not load the extension at all for those surfaces.
   */
  enableTables?: boolean
  /**
   * Let a checkbox click stand while the editor is READ-ONLY, instead of
   * TipTap reverting it. Default `false`, which is what every surface
   * without a persistence path wants — a card description in display mode
   * would otherwise tick a box that nothing saves.
   *
   * This is a permission gate, NOT the persistence path. TipTap hands
   * `onReadOnlyChecked` only `(node, checked)` — no position, no element —
   * so the surface that opts in also owns a listener that resolves WHICH
   * item was clicked and writes it. Today that pairing is
   * `useViewModeTaskToggle` on the wiki doc page; see its header comment
   * for why the work can't live in this callback.
   */
  allowReadOnlyTaskToggle?: boolean
  /**
   * Phase 4d (chat composer / chat read view) — extra TipTap extensions
   * appended after the canonical set. Used for surfaces that need an
   * additional mention node type (e.g. the chat composer ships a second
   * `Mention` instance with node name `channelMention` for `#channel`
   * triggers, alongside the existing `@user` mention extension).
   *
   * **Critical pitfall**: a second `Mention` extension instance MUST have
   * a unique `name` so ProseMirror keeps the two node types distinct
   * (`buildChannelMentionExtension` in `src/features/chat/lib/` does this
   * via `Mention.extend({ name: 'channelMention' })`). Re-using the
   * default `mention` name silently merges the two; the second extension
   * loses its config and only one trigger char works.
   *
   * Use sparingly — the canonical set is the source of truth for ADR-003
   * markdown round-trip safety; anything added here is the caller's
   * responsibility to round-trip-verify.
   */
  extraExtensions?: ReadonlyArray<Extension | TiptapNode | Mark>
  /**
   * Placeholder text shown in the empty rich-mode editor.
   * Wired through `@tiptap/extension-placeholder` so the chat composer's
   * "Message #general" / "Message Omkar" hints render in TipTap-mode (not
   * just the textarea fallback). Empty / undefined leaves the editor
   * blank-but-empty (matches v1 behavior).
   */
  placeholder?: string
  /**
   * Optional `suggestion` config for the `[[wikilink]]` autocomplete
   * (Wave C3). When set, typing `[[` inside the editor opens a picker.
   * Omit on read-only surfaces and call-sites that don't expose
   * wikilink authoring (chat composer, card descriptions, comments —
   * wikilink is opt-in per-MarkdownEditor-instance; today only the
   * Wiki doc page wires it).
   *
   * Round-trip parsing is unconditional: even on surfaces without the
   * picker, a markdown body containing `[[id|name]]` still hydrates
   * into a chip on render, and serializes back unchanged on save. The
   * suggestion config gates the AUTHORING UI only — the parser /
   * serializer is always live so existing wikilinks render everywhere.
   */
  wikilinkSuggestion?: MentionOptions['suggestion']
  /**
   * Phase 4a — caller surface tag. `'wiki'` registers the slash-command
   * extension; everything else (default `'card'`) leaves it off so `/`
   * typed in a card description / comment / chat composer is just a
   * literal `/`.
   *
   * The slash extension only fires if its companion `slashCommandSuggestion`
   * is also provided. Other surfaces can't accidentally surface the menu
   * because they don't pass the suggestion config.
   */
  surface?: EditorSurface
  /**
   * Phase 4a — `@tiptap/suggestion`-shaped config for the slash menu.
   * Built via `buildSlashCommandSuggestion({ commands })` in
   * `src/features/wiki/lib/slash-command-suggestion.ts`. Only loaded
   * when `surface === 'wiki'`.
   */
  slashCommandSuggestion?: MentionOptions['suggestion']
  /**
   * Phase 4a — getter for the localized "Type / to insert a block"
   * empty-paragraph hint. Re-evaluated on every plugin tick so a locale
   * switch propagates without rebuilding the editor (mirrors the
   * `wikilinkSuggestion.items` getter pattern). Only loaded when
   * `surface === 'wiki'`; other surfaces never decorate empty
   * paragraphs so the option is ignored there.
   */
  getSlashHintTranslation?: () => string
  /**
   * Enables auto-detection of typed card references (`MOTE-30` + a space
   * becomes a work-item chip). Omit and the node is still registered — an
   * existing chip renders and round-trips everywhere — but nothing new is
   * ever detected, which is what read-only surfaces and the headless
   * round-trip tests want.
   *
   * Requires BOTH halves: the tenant's project codes (a free pre-filter
   * that keeps `COVID-19` from firing a request) and a resolver that
   * confirms the work item exists before any conversion happens. See
   * `work-item-autodetect.ts` for why verification precedes conversion.
   */
  workItemAutodetect?: WorkItemAutodetectOptions
}

/**
 * Build the extension array. Returns a fresh array per call so each
 * Editor instance gets its own configured extension instances (TipTap
 * doesn't like sharing extension instances across editors).
 */
export function buildEditorExtensions(options: BuildEditorExtensionsOptions = {}) {
  const surface: EditorSurface = options.surface ?? 'card'
  // Chat can never carry a table: the server's chat validation tier has no
  // table nodes, so a chat body containing one is rejected as richtext_invalid.
  // Forcing it here means the cell content model never has to describe a
  // surface that registers neither headings, rules nor images.
  const enableTables = (options.enableTables ?? true) && surface !== 'chat'
  const includeSlashCommand = surface === 'wiki'
  return [
    StarterKit.configure({
      // Allowed-node set per ADR-003 — drop nothing here that would
      // round-trip cleanly through tiptap-markdown. Blockquote, strike,
      // and horizontalRule are all standard GFM and round-trip cleanly.
      //
      // The four inline marks are disabled HERE and re-registered below
      // as extended variants with position-faithful input rules — the
      // stock rules mis-range (eat characters) or throw near mention
      // chips, and stock code is inclusive (the stuck-code-span bug).
      // Never re-enable these built-ins; see mark-input-rules.ts.
      bold: false,
      code: false,
      italic: false,
      strike: false,
      //
      // Headings: full H1-H6 set, matching Confluence. H1 is NOT
      // reserved for the page title — the title input above the editor
      // is a separate surface (a plain `<input>`), not a heading node,
      // so there's no semantic collision when body content uses H1.
      // Markdown round-trip is `#` through `######` — all six levels
      // serialize cleanly through tiptap-markdown.
      //
      // Chat is the restricted tier: no headings and no horizontal
      // rules (ADR-039 — chat bodies are inline marks + mention +
      // code + lists; the BE validator rejects heading/hr on the chat
      // tier, so the editor must not be able to author them). Typing
      // `# hello` or `---` in chat stays literal text.
      heading: surface === 'chat' ? false : { levels: [1, 2, 3, 4, 5, 6] },
      horizontalRule: surface === 'chat' ? false : undefined,
      // Chat composer: Enter inside a code block must ALWAYS insert a
      // newline so multi-line code (incl. blank lines) can be authored;
      // the ONLY keyboard exit is ArrowDown (exitOnArrowDown default).
      // exitOnTripleEnter stays FALSE — an Enter-count exit steals blank
      // lines (a double-Enter exit shipped briefly and was reverted on
      // staging feedback). Other surfaces (wiki / card / comment) keep
      // the stock triple-Enter convenience since they don't send on Enter.
      codeBlock: surface === 'chat' ? { exitOnTripleEnter: false } : undefined,
      // Surfaces that sit under a pointer-events-none OS-file drop overlay
      // (chat composer via `<ChatDropZone>`; card description + comment
      // composer via `useOsFileDrop` + `<FileDropOverlay>`) let an OS drag
      // pass THROUGH to this contenteditable, and ProseMirror's dropcursor
      // plugin paints its insertion line (a solid `currentcolor` bar)
      // inside the editor. File drops are handled entirely by the overlay
      // (attached to the card/comment, or inline-inserted at the caret —
      // never at the drop point) and in-editor node dragging is a
      // non-feature on these surfaces, so the dropcursor has no purpose
      // here — disable it to kill the stray line. The wiki surface keeps
      // it (image / node drag positioning inside the doc body) but with a
      // class handle: OS-file drops insert at the CARET, not the drop
      // point, so `global.css` hides `.wiki-dropcursor` while the doc
      // pane carries `data-os-file-drag` (set synchronously on dragenter
      // by the doc drop hook — the line must never paint, not even for
      // one frame, or it promises drop-point insertion). In-editor node
      // drags never set the attr, so their cursor UX is untouched.
      dropcursor:
        surface === 'chat' || surface === 'card' || surface === 'comment'
          ? false
          : { class: 'wiki-dropcursor' },
    }),
    // Position-faithful input-rule replacements for the four StarterKit
    // marks disabled above. Registered immediately after StarterKit so
    // the schema's mark rank order (bold < code < italic < underline <
    // strike — StarterKit's own push order, with underline slotted before
    // strike so nesting follows the B I U S toolbar order) is preserved;
    // mark rank drives DOM nesting order on serialize, so moving these
    // breaks byte-stable output. See mark-input-rules.ts for the why.
    BoldWithFaithfulRules,
    CodeWithFaithfulRules,
    ItalicWithFaithfulRules,
    // Underline has no input rule on purpose: `__text__` is bold in GFM
    // and every other delimiter collides with sub / sup / strike.
    UnderlineWithMarkdown,
    StrikeWithFaithfulRules,
    // Backspace at the start of a list item removes its marker instead of
    // folding it into the item above, and `- ` / `1. ` at the start of an
    // item switch that item's list kind. See list-item-editing.ts.
    ListItemEditing,
    // Toolbar-toggled marks persist while typing despite code's
    // inclusive:false (the toggle otherwise marked only one character).
    StickyStoredMarks,
    // Chat-only code-block ergonomics: ``` opens a block on the third
    // backtick (no Enter), Enter inside NEVER exits (ArrowDown is the only
    // exit). Plain Extension — no schema names (fixture stays identical).
    ...(surface === 'chat' ? [ChatCodeBlockBehavior] : []),
    // LinkWithClickToEdit extends the canonical TipTap Link extension
    // with a ProseMirror handleClick plugin that opens the existing
    // <LinkEditorPopover> when the user plain-clicks a rendered <a>.
    // Cmd/Ctrl+click + middle-click stay native so "open in new tab"
    // via modifier keys still works. `openOnClick: false` keeps the
    // browser from navigating on every click (the plugin owns the
    // click intent now).
    // `defaultProtocol: 'https'` — a bare domain (`3mote.com`) must
    // autolink to https: most sites are https-only and the BE's unfurl
    // Lambda cannot reliably egress on port 80, so an `http://` href
    // produced a dead link preview. iOS and Android already upgrade bare
    // domains to https at compose time, so all three clients now agree.
    // An explicitly typed scheme (`http://intranet.local`) is kept as is.
    LinkWithClickToEdit.configure({ openOnClick: false, defaultProtocol: 'https' }),
    // Image extension is surface-conditional. The wiki surface gets the
    // `WikiImageNodeView` (upload spinner + signed-GET resolution +
    // lightbox + error fallback). Card descriptions + comments get the
    // leaner `CardImageNodeView`, which also resolves signed GET URLs from
    // an `attachmentId` (so an embed survives a reload past the ~5-min
    // signed-`src` TTL), paints an instant blob preview + spinner while a
    // dropped / picked image uploads, and caps the render size — but with
    // NO lightbox / cancel-upload orchestration (cards don't mount the
    // wiki `WikiEmbedOrchestrator`). Chat has NO image node at all: chat
    // images ride the attachments table, never the message body, and the
    // chat validation tier rejects the node.
    ...(surface === 'chat'
      ? []
      : [
          surface === 'wiki'
            ? buildWikiImageExtension({ view: WikiImageNodeView })
            : surface === 'card' || surface === 'comment'
              ? buildCardImageExtension({ view: CardImageNodeView })
              : Image,
        ]),
    // Inline file-embed chip for NON-IMAGE files on the card / comment
    // surfaces. Mirrors the card image node (attachment-backed, resolves a
    // fresh signed GET URL on click) but for pdf / zip / csv / … — the chip
    // downloads on click instead of opening a lightbox. The wiki surface
    // uses its own `buildFileEmbedExtension` (same `fileEmbed` node name,
    // wired to the wiki orchestrator); chat has no file node (chat files
    // ride the attachments table, never the message body).
    ...(surface === 'card' || surface === 'comment'
      ? [buildCardFileEmbedExtension({ view: CardFileEmbedView })]
      : []),
    // Inline video player for the card / comment surfaces. SAME node and
    // attr contract as the wiki embed — the BE validation tier and its
    // walkers key on `videoEmbed.attrs.fileId` / `.caption`, so a second
    // spelling would repeat the `fileEmbed` `attachmentId` split. Only
    // the NodeView is card-flavoured: `uploads`-namespace copy,
    // `card-embed:*` upload events (the wiki orchestrator is not mounted
    // on a card and must not be asked to inspect every card click),
    // column-width sizing, and a caption that is editable only while the
    // editor is. Chat has no video node — chat video rides the
    // attachments table and the chat tier rejects the node.
    ...(surface === 'card' || surface === 'comment'
      ? [buildVideoEmbedExtension({ view: CardVideoEmbedView })]
      : []),
    // Subscript + Superscript marks — `~text~` and `^text^` markdown.
    // Round-trip via `markdown-it-sub` / `markdown-it-sup` plugins
    // registered on tiptap-markdown's parser through the per-extension
    // storage hook on the wrappers above. See ADR-003 for the
    // allowed-node set inclusion.
    SubscriptWithMarkdown,
    SuperscriptWithMarkdown,
    // Task lists are block-structure the chat tier rejects — a chat
    // message is not a checklist surface (Slack parity); `[ ]` typed
    // in chat stays literal text.
    ...(surface === 'chat'
      ? []
      : [
          TaskList,
          TaskItem.configure({
            nested: true,
            // Present only when the surface has somewhere to persist the
            // flip. Absent (the default), TipTap reverts a read-only
            // click, which is the right answer for a display-mode card
            // description. Returning `true` accepts the DOM state; the
            // opted-in surface's own listener does the writing.
            ...(options.allowReadOnlyTaskToggle ? { onReadOnlyChecked: () => true } : {}),
          }),
        ]),
    // Tables only on full-editor surfaces. Comment composer omits them
    // (see `enableTables` doc on BuildEditorExtensionsOptions).
    //
    // A cell holds a per-SURFACE list of block types — on the wiki that
    // includes callout panels, expand sections and video embeds, so every
    // slash command an author can reach inside a cell actually lands. The
    // list and the reasoning live in `nodes/table/table-extensions.ts`; the
    // short version is that a one-paragraph cell made Enter a no-op and
    // made every multi-block paste (Confluence, Docs, Word) spill its
    // overflow into the neighbouring cell. It is a factory rather than a
    // constant because naming a wiki-only node on the card schema throws.
    //
    // The wiki variants also carry two custom attribute axes — per-cell
    // `textAlign` and table-level `headerBgColor` — driven by the cell
    // bubble menu + the <TableFooterPopover>. Both attributes are no-ops
    // when unset, so every call-site that opts into tables gets the same
    // visual baseline.
    //
    // `WikiTableRow` exists only to carry a markdown serializer, so a
    // PARTIAL cell selection (one cell, one column, a few body rows)
    // copies as a valid GFM fragment. A bare row has no `table` wrapper,
    // and `tiptap-markdown` ships no row/cell spec at all, so without it
    // the clipboard's plain-text half is the literal string `[tableRow]`.
    ...(enableTables
      ? [
          WikiTable.configure({
            resizable: true,
            // Emits `.tableWrapper` from `renderHTML` too, not just from
            // the resize node view — so the horizontal-scroll rule
            // applies on every render path, including a read-only card
            // description and the clipboard's HTML half.
            renderWrapper: true,
            lastColumnResizable: true,
            handleWidth: 6,
            // The real floor for a drag. The CSS `min-width` on cells is
            // ignored under `table-layout: fixed`.
            cellMinWidth: 64,
          }),
          WikiTableRow,
          buildWikiTableHeader(surface),
          buildWikiTableCell(surface),
          // Backspace out of an empty block at the top of a cell, which
          // the default binding answers by doing nothing.
          TableCellBackspace,
        ]
      : []),
    buildMentionExtension({ suggestion: options.mentionSuggestion }),
    // Wikilink — `[[id|name]]` inline references (Wave C3). The parser
    // + serializer are always loaded so existing wikilinks render +
    // round-trip on every editor surface; the suggestion config is
    // opt-in per-call-site so only the wiki doc page (today) shows a
    // picker on `[[` typing.
    buildWikiLinkExtension({ suggestion: options.wikilinkSuggestion }),
    // Slash-menu (Phase 4a) — only loaded on the wiki editor surface.
    // The extension itself is a Mention-extend with name 'slashCommand',
    // distinct from `mention` + `wikilink`. See
    // `src/editor/slash-command-extension.ts` for the allow predicate +
    // command override.
    ...(includeSlashCommand
      ? [buildSlashCommandExtension({ suggestion: options.slashCommandSuggestion })]
      : []),
    // Wiki-only callout panels (Phase 4a Wave 2). Six block-level
    // Node extensions; the 5 named kinds share a factory + a single
    // shared NodeView component bound to per-kind visual spec, the
    // custom panel ships color + icon attributes that the bubble
    // menu picker (mounted by the wiki MarkdownEditor instance)
    // updates. Schema constraint: `content: 'block+'` excludes other
    // block names that aren't listed here, so nesting another panel
    // inside is impossible at the schema level — the slash-command
    // run also bails when the cursor is already in a panel as a
    // belt-and-braces guard (panel-in-panel inserts are silent
    // no-ops, matching the locked decision).
    ...(includeSlashCommand
      ? [
          InfoPanel,
          NotePanel,
          SuccessPanel,
          WarningPanel,
          ErrorPanel,
          buildCustomPanelExtension({ view: CustomPanelView }),
        ]
      : []),
    // Wiki-only paragraph-id widening. Adds an `id: string | null`
    // attribute to every `paragraph` node that renders as
    // `data-paragraph-id` on the DOM, so comments anchored to a paragraph
    // can resolve their host element on click-through. The id rides
    // through the JSON save automatically; back-filling missing ids on
    // existing docs happens in the editor consumer (see <DocPage>).
    ...(includeSlashCommand ? [ParagraphIdExtension] : []),
    // Wiki-only inline anchored comments (ADR-037). Renders highlights
    // for non-resolved anchored threads as ProseMirror Decorations (not
    // persisted marks — so comment-only reviewers can anchor without a
    // body write) and bridges the comment-on-selection / open-thread
    // intents to React via window events. Depends on ParagraphIdExtension
    // above for the stable paragraph `id` the anchors resolve against;
    // also registers the ⌘⌥M shortcut.
    ...(includeSlashCommand ? [buildInlineCommentExtension()] : []),
    // Wiki-only Phase 4b structural + inline-atomic blocks. Expand is
    // block-level (collapsible section with summary + body); status +
    // date are inline atoms editable via in-place popovers (no
    // contenteditable child — `atom: true`). Table + TaskList are
    // already registered in the canonical set above; the slash entries
    // for those drive the existing extensions, no new factory needed.
    ...(includeSlashCommand
      ? [
          buildExpandSectionExtension({ view: ExpandSectionView }),
          buildStatusLozengeExtension({ view: StatusLozengeView }),
          buildDateChipExtension({ view: DateChipView }),
        ]
      : []),
    // The work-item chip is registered on EVERY surface, not just wiki.
    // It began as a wiki slash-menu embed, but auto-detecting a typed
    // card reference (`MOTE-30`) has to work wherever people write about
    // work — a card description, a comment, a chat message. Only the
    // slash-menu PICKER stays wiki-only (see the entry above); the node
    // itself, its NodeView, and the detection rule are cross-surface.
    buildWorkItemEmbedExtension({ view: WorkItemEmbedView }),
    // Detection is a SEPARATE extension from the node, and contributes no
    // schema names of its own — so wiring it on a surface never changes
    // that surface's node/mark set (the `richtext-surface-drift` fixture
    // stays untouched). Same split as `ChatCodeBlockBehavior`.
    ...(options.workItemAutodetect
      ? [buildWorkItemAutodetectExtension(options.workItemAutodetect)]
      : []),
    // Wiki-only Phase 4c embed blocks. fileEmbed is an inline atomic
    // chip; videoEmbed is a block atom. The mention + emoji + image
    // slash entries don't ship new nodes — mention reuses the canonical
    // Mention extension above; emoji opens a popover and inserts a
    // Unicode glyph (wrapped in the WikiEmojiMark below so the editor
    // renders it at a larger font-size); image drives the existing
    // TipTap Image extension via the same upload flow the toolbar uses.
    ...(includeSlashCommand
      ? [
          buildVideoEmbedExtension({ view: VideoEmbedView }),
          buildFileEmbedExtension({ view: FileEmbedView }),
          // Slash-inserted emoji visual treatment — bumps font-size via
          // `.wiki-emoji` CSS class in `global.css`. Markdown serializer
          // emits the wrapped text without any syntax so saved docs stay
          // clean Unicode; the mark survives only within the live edit
          // session (acceptable per the "make freshly-inserted emoji
          // bigger" v1 scope).
          WikiEmojiMark,
        ]
      : []),
    // Trailing-paragraph guarantee — appends an empty paragraph to the
    // end of the doc when the last block is anything other than a
    // paragraph. Eliminates the gap-cursor surface for "click below the
    // last block" and "ArrowDown out of the last row / panel": both land
    // in a real text cursor instead of the unfamiliar horizontal-line
    // blink, which reads as a broken caret rather than as a position.
    // Every surface that can put a table, an image or a callout last
    // needs it; chat is excluded because its composer authors none of
    // those and a trailing empty paragraph there would be sent.
    ...(surface === 'chat' ? [] : [TrailingParagraphExtension]),
    // Empty-paragraph slash hint (Phase 4a polish) — decorates every
    // non-first empty paragraph with `data-slash-hint=<localized>`.
    // The CSS rule `content: attr(data-slash-hint)` in `global.css`
    // reads the attribute, so a locale switch propagates without
    // touching the CSS. Only loaded on the wiki surface; the
    // translation getter falls through to an empty string when
    // omitted so the CSS rule renders nothing.
    ...(includeSlashCommand && options.getSlashHintTranslation
      ? [
          EmptyParagraphHintExtension.configure({
            getTranslation: options.getSlashHintTranslation,
          }),
        ]
      : []),
    ...(options.extraExtensions ?? []),
    // Always load Placeholder — even when no initial value is known —
    // so a downstream `useEffect` can update its `options.placeholder`
    // imperatively once the parent's async data settles (e.g. the chat
    // composer's "Message #general" derives from a useConversation call
    // that resolves AFTER the editor has mounted).
    Placeholder.configure({
      placeholder: options.placeholder ?? '',
      showOnlyWhenEditable: true,
      showOnlyCurrent: true,
    }),
    Markdown.configure({
      html: false,
      tightLists: true,
      bulletListMarker: '-',
      linkify: false,
      breaks: false,
      // Paste from clipboard: when the user pastes plain text that looks
      // like markdown source, parse it through `markdown-it` and insert
      // the resulting ProseMirror nodes instead of dropping the raw text
      // verbatim. Unsupported syntaxes (markdown-it plugins like
      // footnotes / def lists / emoji shortcodes / sub-sup / abbrs /
      // containers / smartypants typographer replacements) fall through
      // as literal text — that's acceptable, the spike (ADR-003) locked
      // the allowed-node set deliberately.
      transformPastedText: true,
      // Copy from editor: serialize the selection back to markdown on
      // copy so pasting into another markdown-aware tool (Slack,
      // GitHub, another wiki page) preserves formatting. Round-trip
      // pairs with `transformPastedText` above.
      transformCopiedText: true,
    }),
  ]
}
